import {css} from "@panda/css";
import {parse} from "date-fns";

import {Button} from "@pg-design/button-module";
import {Link} from "@pg-design/link-module";
import {Highlight, Text} from "@pg-design/text-module";
import {appendQueryString} from "@pg-mono/request";
import {formatPL, phoneNumberFormat} from "@pg-mono/string-utils";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {InquiryButtonTypeName} from "../../../constants/inquiry_button_type";
import {ApplicationModalButton} from "../../application/components/ApplicationModalButton";
import {GTMEventApplicationSourceSection} from "../../application/types/lead/GTMEvenApplicationSourceSection";
import {ApplicationSource, ApplicationSourceSection} from "../../application/utils/form/application_form_source";
import {Country} from "../../region/types/Country";
import {offerViewExternalSiteClick, propertyViewExternalSiteClick} from "../../tracking/algolytics_hits/external_site_click";
import {ITrackedProperty} from "../../tracking/algolytics_hits/tracked_utils/tracked_property";
import {trackAmplitudeButtonClick} from "../../tracking/amplitude/track_amplitude_button_click";
import {getThumbnailLink} from "../../utils/get_thumbnail_link";
import {OfferDisplayType} from "../helpers/OfferDisplayType";
import {IOfferAssistant} from "../types/IOfferAssistant";
import {IOfferTrader} from "../types/IOfferTrader";
import {ISalesOffice} from "../types/ISalesOffice";
import {getOfferDisplayStatus} from "../utils/get_offer_display_status";

interface IProps {
    offer: {
        assistant: IOfferAssistant | null;
        configuration: {
            include_utm: boolean;
            phone_clip: string | null;
            display_type: OfferDisplayType;
            limited_presentation?: boolean;
            is_autoresponder_enabled: boolean;
        };
        geo_point: {
            coordinates: [number, number];
        };
        id: number;
        is_phone_clip_enabled: boolean;
        logo_url: string | null;
        name: string;
        region: {
            country: Country;
            full_name: string;
            id: number;
        };
        sales_offices: ISalesOffice[];
        traders: IOfferTrader[];
        type: number;
        vendor: {
            id: number;
            name: string;
            slug: string;
            logo_url: string | null;
        };
        website: string | null;
        is_active?: boolean;
    };
    property?: ITrackedProperty;
}

const cleanUrl = (url: string): string => {
    const cleanedUrl = url.split("#")[0].split("?")[0];

    let formattedUrl = cleanedUrl.replace(/^(https?:\/\/)/, "");

    if (!formattedUrl.startsWith("www.")) {
        formattedUrl = `www.${formattedUrl}`;
    }

    return formattedUrl.replace(/\/$/, "");
};

const renderPhoneButton = (phone: string) => {
    return (
        <div className={css({mb: 3})}>
            <Link href={`tel:+${phone}`} data-testid={OFFER_TEST_IDS.OFFER_VIEW.SALES_OFFICE_PHONE_NUMBER}>
                {phoneNumberFormat(phone)}
            </Link>
        </div>
    );
};

const renderOpeningTimes = (lower: string, upper: string) => {
    const formattedTime = {
        lower: formatPL(parse(lower, "HH:mm:ss", new Date()), "HH:mm"),
        upper: formatPL(parse(upper, "HH:mm:ss", new Date()), "HH:mm")
    };

    return (
        <span>
            {formattedTime.lower} - {formattedTime.upper}
        </span>
    );
};

const renderSalesOffice = (salesOffice: ISalesOffice, isForSaleOverLimit: boolean, isPhoneClipEnabled: boolean) => {
    const isOpenHours =
        salesOffice.open_hours_1 ||
        salesOffice.open_hours_2 ||
        salesOffice.open_hours_3 ||
        salesOffice.open_hours_4 ||
        salesOffice.open_hours_5 ||
        salesOffice.open_hours_6 ||
        salesOffice.open_hours_7;
    const isNote = salesOffice.info_note_1 || salesOffice.info_note_2 || salesOffice.info_note_3;
    const address = salesOffice.address
        .split("\n")
        .map((e) => e.trim())
        .join(" ");

    return (
        <>
            <div className={salesOfficeInfo}>
                <Text align="center" variant="body_copy_1" className={css({mb: 3})}>
                    Adres biura sprzedaży:
                </Text>

                <address className="bt" data-testid={OFFER_TEST_IDS.OFFER_VIEW.SALES_OFFICE_ADDRESS}>
                    {address}
                </address>
            </div>

            {isOpenHours && (
                <>
                    <Text align="center" variant="body_copy_1" className={css({mb: 3})}>
                        Godziny otwarcia biura sprzedaży:
                    </Text>

                    <ul className={openHoursList} data-testid={OFFER_TEST_IDS.OFFER_VIEW.SALES_OFFICE_OPENING_HOURS}>
                        {salesOffice.open_hours_1?.lower && salesOffice.open_hours_1?.upper && (
                            <li>
                                <Text as="span" variant="body_copy_2">
                                    <span>Poniedziałek: </span>
                                    {renderOpeningTimes(salesOffice.open_hours_1.lower, salesOffice.open_hours_1.upper)}
                                </Text>
                            </li>
                        )}

                        {salesOffice.open_hours_2?.lower && salesOffice.open_hours_2?.upper && (
                            <li>
                                <Text as="span" variant="body_copy_2">
                                    <span>Wtorek: </span>
                                    {renderOpeningTimes(salesOffice.open_hours_2.lower, salesOffice.open_hours_2.upper)}
                                </Text>
                            </li>
                        )}

                        {salesOffice.open_hours_3?.lower && salesOffice.open_hours_3?.upper && (
                            <li>
                                <Text as="span" variant="body_copy_2">
                                    <span>Środa: </span>
                                    {renderOpeningTimes(salesOffice.open_hours_3.lower, salesOffice.open_hours_3.upper)}
                                </Text>
                            </li>
                        )}

                        {salesOffice.open_hours_4?.lower && salesOffice.open_hours_4?.upper && (
                            <li>
                                <Text as="span" variant="body_copy_2">
                                    <span>Czwartek: </span>
                                    {renderOpeningTimes(salesOffice.open_hours_4.lower, salesOffice.open_hours_4.upper)}
                                </Text>
                            </li>
                        )}

                        {salesOffice.open_hours_5?.lower && salesOffice.open_hours_5?.upper && (
                            <li>
                                <Text as="span" variant="body_copy_2">
                                    <span>Piątek: </span>
                                    {renderOpeningTimes(salesOffice.open_hours_5.lower, salesOffice.open_hours_5.upper)}
                                </Text>
                            </li>
                        )}

                        {salesOffice.open_hours_6?.lower && salesOffice.open_hours_6?.upper && (
                            <li>
                                <Text as="span" variant="body_copy_2">
                                    <span>Sobota: </span>
                                    {renderOpeningTimes(salesOffice.open_hours_6.lower, salesOffice.open_hours_6.upper)}
                                </Text>
                            </li>
                        )}

                        {salesOffice.open_hours_7?.lower && salesOffice.open_hours_7?.upper && (
                            <li>
                                <Text as="span" variant="body_copy_2">
                                    <span>Niedziela: </span>
                                    {renderOpeningTimes(salesOffice.open_hours_7.lower, salesOffice.open_hours_7.upper)}
                                </Text>
                            </li>
                        )}
                    </ul>
                </>
            )}

            {isNote && (
                <div className={noteHolder}>
                    {salesOffice.info_note_1 && (
                        <Text className={css({color: "var(--colors-gray-700)", mb: 2})} align="center" variant="info_txt_1">
                            {salesOffice.info_note_1}
                        </Text>
                    )}

                    {salesOffice.info_note_2 && (
                        <Text className={css({color: "var(--colors-gray-700)", mb: 2})} align="center" variant="info_txt_1">
                            {salesOffice.info_note_2}
                        </Text>
                    )}

                    {salesOffice.info_note_3 && (
                        <Text className={css({color: "var(--colors-gray-700)", mb: 2})} align="center" variant="info_txt_1">
                            {salesOffice.info_note_3}
                        </Text>
                    )}
                </div>
            )}

            {!isForSaleOverLimit && (
                <>
                    {!isPhoneClipEnabled && salesOffice.phone && renderPhoneButton(salesOffice.phone)}

                    {!isPhoneClipEnabled && salesOffice.mobile && renderPhoneButton(salesOffice.mobile)}
                </>
            )}
        </>
    );
};

export const OfferSalesOffices = (props: IProps) => {
    const {offer, property} = props;
    const {vendor} = props.offer;
    const isForSaleOverLimit = getOfferDisplayStatus(offer) === "limited";
    const hideForm = offer.sales_offices.length === 0;

    const firstSalesOffice = props.offer.sales_offices && props.offer.sales_offices[0];
    const gtmData = {
        clickText: InquiryButtonTypeName.ARRANGE_MEETING,
        source: GTMEventApplicationSourceSection.OFFER_WEBSITE,
        vendorName: offer.vendor.name ?? offer.vendor.id.toString(),
        offerName: offer.name ?? offer.id.toString()
    };

    return (
        <div className={offerSalesOfficesBase} data-testid={OFFER_TEST_IDS.OFFER_VIEW.OFFER_SALES_OFFICE_INFO_SECTION}>
            <div>
                {getThumbnailLink(offer.logo_url, 200, 200) ? (
                    <div className={css({display: "flex", justifyContent: "center", mb: 3})}>
                        <img
                            src={getThumbnailLink(offer.logo_url, 200, 200) ?? ""}
                            alt={`${offer.name} logo`}
                            width={160}
                            height={120}
                            loading="lazy"
                            data-testid={OFFER_TEST_IDS.OFFER_VIEW.VENDOR_LOGO}
                        />
                    </div>
                ) : (
                    <>
                        {getThumbnailLink(vendor.logo_url, 160, 120) && (
                            <img
                                src={getThumbnailLink(vendor.logo_url, 160, 120) ?? ""}
                                alt={`${vendor.name} logo`}
                                width={160}
                                height={120}
                                data-testid={OFFER_TEST_IDS.OFFER_VIEW.VENDOR_LOGO}
                            />
                        )}

                        <Text
                            align="center"
                            as="p"
                            variant="headline_5"
                            className={css({my: 3})}
                            dataTestId={OFFER_TEST_IDS.OFFER_VIEW.SALES_OFFICE_VENDOR_NAME}
                        >
                            {vendor.name}
                        </Text>
                    </>
                )}

                {offer.assistant ? (
                    <>
                        <Text variant="headline_6" as="p" className={css({my: 0.5})} align="center">
                            {offer.assistant.name}
                        </Text>
                        <Text variant="body_copy_2" as="p" className={css({mb: 2, mt: 0.5})} align="center">
                            OPIEKUN INWESTYCJI
                        </Text>
                    </>
                ) : null}

                {offer.website && (
                    <LinkHelper offer={offer} property={property} dataTestId={OFFER_TEST_IDS.OFFER_VIEW.OFFER_WEBSITE_LINK}>
                        <Highlight>
                            <Text variant="button_medium">Strona internetowa inwestycji</Text>
                        </Highlight>
                    </LinkHelper>
                )}

                {offer.website && (
                    <div className={css({mb: 2.5})}>
                        <LinkHelper offer={offer} property={property}>
                            <Text
                                className={offerLinkAsText}
                                variant="body_copy_2"
                                as="p"
                                align="center"
                                dataTestId={OFFER_TEST_IDS.OFFER_VIEW.OFFER_WEBSITE_URL}
                            >
                                {cleanUrl(offer.website)}
                            </Text>
                        </LinkHelper>
                    </div>
                )}

                {firstSalesOffice && renderSalesOffice(firstSalesOffice, isForSaleOverLimit, offer.is_phone_clip_enabled)}

                {!isForSaleOverLimit && offer.is_phone_clip_enabled && offer.configuration.phone_clip && renderPhoneButton(offer.configuration.phone_clip)}

                {!isForSaleOverLimit && !hideForm && (
                    <div className={css({display: "flex", justifyContent: "center"})}>
                        <ApplicationModalButton
                            source={property ? ApplicationSource.ApplicationPropertyScheduleMeeting : ApplicationSource.ApplicationOfferScheduleMeeting}
                            sourceSection={ApplicationSourceSection.BOTTOM}
                            vendorId={props.offer.vendor.id}
                            offerId={props.offer.id}
                            propertyId={property?.id ?? null}
                            forceClose={getOfferDisplayStatus(props.offer) === "limited"}
                            salesOfficeId={firstSalesOffice && firstSalesOffice.id}
                            disableUnlockChoice
                            render={({openModal}) => (
                                <Button
                                    variant="outlined_secondary"
                                    onClick={() => openModal(gtmData)}
                                    dataTestId={OFFER_TEST_IDS.OFFER_VIEW.MAKE_AN_APPOINTMENT_BUTTON}
                                >
                                    {InquiryButtonTypeName.ARRANGE_MEETING}
                                </Button>
                            )}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

const LinkHelper = ({
    offer,
    property,
    children,
    dataTestId
}: {
    offer: IProps["offer"];
    property: IProps["property"] | null;
    children: React.ReactNode;
    dataTestId?: string;
}) => {
    if (!offer.website) return null;

    const href = offer.configuration.include_utm
        ? appendQueryString(offer.website, {
              utm_campaign: "prezentacja",
              utm_medium: "referral",
              utm_source: "rynekpierwotny"
          })
        : offer.website;

    return (
        <Link
            className={css({mb: 1.5, display: "inline-block"})}
            href={href}
            target="_blank"
            rel="nofollow"
            data-gtm-click="Odwiedź stronę inwestycji"
            data-testid={dataTestId}
            onClick={() => {
                if (property) {
                    propertyViewExternalSiteClick(property, offer, offer.vendor);
                } else {
                    offerViewExternalSiteClick(offer, offer.vendor);
                }

                trackAmplitudeButtonClick({
                    buttonName: "view_developer_website",
                    value: "see_developer_website",
                    buttonLink: href,
                    buttonCategory: property ? "property_page" : "investment_page",
                    itemId: offer.id,
                    propertyId: property?.id
                });
            }}
        >
            {children}
        </Link>
    );
};

const offerSalesOfficesBase = css({
    p: 3,
    borderRadius: 2,
    backgroundColor: "var(--colors-white)",
    textAlign: "center"
});

const offerLinkAsText = css({
    display: "inline",
    background: "var(--colors-gray-100)",
    borderRadius: 1,
    padding: "0.4rem 0.8rem"
});

const salesOfficeInfo = css({
    mb: 3
});

const noteHolder = css({
    maxWidth: "50rem",
    margin: "0 auto 2.4rem auto"
});

const openHoursList = css({
    listStyle: "none",
    paddingLeft: 0,
    margin: "0 auto 2.4rem",
    "& > li": {
        padding: "0.2rem 0"
    }
});
import {css} from "@panda/css";
import {parse} from "date-fns";

import {Button} from "@pg-design/button-module";
import {Link} from "@pg-design/link-module";
import {Highlight, Text} from "@pg-design/text-module";
import {appendQueryString} from "@pg-mono/request";
import {formatPL, phoneNumberFormat} from "@pg-mono/string-utils";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {InquiryButtonTypeName} from "../../../constants/inquiry_button_type";
import {ApplicationModalButton} from "../../application/components/ApplicationModalButton";
import {GTMEventApplicationSourceSection} from "../../application/types/lead/GTMEvenApplicationSourceSection";
import {ApplicationSource, ApplicationSourceSection} from "../../application/utils/form/application_form_source";
import {Country} from "../../region/types/Country";
import {offerViewExternalSiteClick, propertyViewExternalSiteClick} from "../../tracking/algolytics_hits/external_site_click";
import {ITrackedProperty} from "../../tracking/algolytics_hits/tracked_utils/tracked_property";
import {trackAmplitudeButtonClick} from "../../tracking/amplitude/track_amplitude_button_click";
import {getThumbnailLink} from "../../utils/get_thumbnail_link";
import {OfferDisplayType} from "../helpers/OfferDisplayType";
import {IOfferAssistant} from "../types/IOfferAssistant";
import {IOfferTrader} from "../types/IOfferTrader";
import {ISalesOffice} from "../types/ISalesOffice";
import {getOfferDisplayStatus} from "../utils/get_offer_display_status";

interface IProps {
    offer: {
        assistant: IOfferAssistant | null;
        configuration: {
            include_utm: boolean;
            phone_clip: string | null;
            display_type: OfferDisplayType;
            limited_presentation?: boolean;
            is_autoresponder_enabled: boolean;
        };
        geo_point: {
            coordinates: [number, number];
        };
        id: number;
        is_phone_clip_enabled: boolean;
        logo_url: string | null;
        name: string;
        region: {
            country: Country;
            full_name: string;
            id: number;
        };
        sales_offices: ISalesOffice[];
        traders: IOfferTrader[];
        type: number;
        vendor: {
            id: number;
            name: string;
            slug: string;
            logo_url: string | null;
        };
        website: string | null;
        is_active?: boolean;
    };
    property?: ITrackedProperty;
}

const cleanUrl = (url: string): string => {
    const cleanedUrl = url.split("#")[0].split("?")[0];

    let formattedUrl = cleanedUrl.replace(/^(https?:\/\/)/, "");

    if (!formattedUrl.startsWith("www.")) {
        formattedUrl = `www.${formattedUrl}`;
    }

    return formattedUrl.replace(/\/$/, "");
};

const renderPhoneButton = (phone: string) => {
    return (
        <div className={css({mb: 3})}>
            <Link href={`tel:+${phone}`} data-testid={OFFER_TEST_IDS.OFFER_VIEW.SALES_OFFICE_PHONE_NUMBER}>
                {phoneNumberFormat(phone)}
            </Link>
        </div>
    );
};

const renderOpeningTimes = (lower: string, upper: string) => {
    const formattedTime = {
        lower: formatPL(parse(lower, "HH:mm:ss", new Date()), "HH:mm"),
        upper: formatPL(parse(upper, "HH:mm:ss", new Date()), "HH:mm")
    };

    return (
        <span>
            {formattedTime.lower} - {formattedTime.upper}
        </span>
    );
};

const renderSalesOffice = (salesOffice: ISalesOffice, isForSaleOverLimit: boolean, isPhoneClipEnabled: boolean) => {
    const isOpenHours =
        salesOffice.open_hours_1 ||
        salesOffice.open_hours_2 ||
        salesOffice.open_hours_3 ||
        salesOffice.open_hours_4 ||
        salesOffice.open_hours_5 ||
        salesOffice.open_hours_6 ||
        salesOffice.open_hours_7;
    const isNote = salesOffice.info_note_1 || salesOffice.info_note_2 || salesOffice.info_note_3;
    const address = salesOffice.address
        .split("\n")
        .map((e) => e.trim())
        .join(" ");

    return (
        <>
            <div className={salesOfficeInfo}>
                <Text align="center" variant="body_copy_1" className={css({mb: 3})}>
                    Adres biura sprzedaży:
                </Text>

                <address className="bt" data-testid={OFFER_TEST_IDS.OFFER_VIEW.SALES_OFFICE_ADDRESS}>
                    {address}
                </address>
            </div>

            {isOpenHours && (
                <>
                    <Text align="center" variant="body_copy_1" className={css({mb: 3})}>
                        Godziny otwarcia biura sprzedaży:
                    </Text>

                    <ul className={openHoursList} data-testid={OFFER_TEST_IDS.OFFER_VIEW.SALES_OFFICE_OPENING_HOURS}>
                        {salesOffice.open_hours_1?.lower && salesOffice.open_hours_1?.upper && (
                            <li>
                                <Text as="span" variant="body_copy_2">
                                    <span>Poniedziałek: </span>
                                    {renderOpeningTimes(salesOffice.open_hours_1.lower, salesOffice.open_hours_1.upper)}
                                </Text>
                            </li>
                        )}

                        {salesOffice.open_hours_2?.lower && salesOffice.open_hours_2?.upper && (
                            <li>
                                <Text as="span" variant="body_copy_2">
                                    <span>Wtorek: </span>
                                    {renderOpeningTimes(salesOffice.open_hours_2.lower, salesOffice.open_hours_2.upper)}
                                </Text>
                            </li>
                        )}

                        {salesOffice.open_hours_3?.lower && salesOffice.open_hours_3?.upper && (
                            <li>
                                <Text as="span" variant="body_copy_2">
                                    <span>Środa: </span>
                                    {renderOpeningTimes(salesOffice.open_hours_3.lower, salesOffice.open_hours_3.upper)}
                                </Text>
                            </li>
                        )}

                        {salesOffice.open_hours_4?.lower && salesOffice.open_hours_4?.upper && (
                            <li>
                                <Text as="span" variant="body_copy_2">
                                    <span>Czwartek: </span>
                                    {renderOpeningTimes(salesOffice.open_hours_4.lower, salesOffice.open_hours_4.upper)}
                                </Text>
                            </li>
                        )}

                        {salesOffice.open_hours_5?.lower && salesOffice.open_hours_5?.upper && (
                            <li>
                                <Text as="span" variant="body_copy_2">
                                    <span>Piątek: </span>
                                    {renderOpeningTimes(salesOffice.open_hours_5.lower, salesOffice.open_hours_5.upper)}
                                </Text>
                            </li>
                        )}

                        {salesOffice.open_hours_6?.lower && salesOffice.open_hours_6?.upper && (
                            <li>
                                <Text as="span" variant="body_copy_2">
                                    <span>Sobota: </span>
                                    {renderOpeningTimes(salesOffice.open_hours_6.lower, salesOffice.open_hours_6.upper)}
                                </Text>
                            </li>
                        )}

                        {salesOffice.open_hours_7?.lower && salesOffice.open_hours_7?.upper && (
                            <li>
                                <Text as="span" variant="body_copy_2">
                                    <span>Niedziela: </span>
                                    {renderOpeningTimes(salesOffice.open_hours_7.lower, salesOffice.open_hours_7.upper)}
                                </Text>
                            </li>
                        )}
                    </ul>
                </>
            )}

            {isNote && (
                <div className={noteHolder}>
                    {salesOffice.info_note_1 && (
                        <Text className={css({color: "var(--colors-gray-700)", mb: 2})} align="center" variant="info_txt_1">
                            {salesOffice.info_note_1}
                        </Text>
                    )}

                    {salesOffice.info_note_2 && (
                        <Text className={css({color: "var(--colors-gray-700)", mb: 2})} align="center" variant="info_txt_1">
                            {salesOffice.info_note_2}
                        </Text>
                    )}

                    {salesOffice.info_note_3 && (
                        <Text className={css({color: "var(--colors-gray-700)", mb: 2})} align="center" variant="info_txt_1">
                            {salesOffice.info_note_3}
                        </Text>
                    )}
                </div>
            )}

            {!isForSaleOverLimit && (
                <>
                    {!isPhoneClipEnabled && salesOffice.phone && renderPhoneButton(salesOffice.phone)}

                    {!isPhoneClipEnabled && salesOffice.mobile && renderPhoneButton(salesOffice.mobile)}
                </>
            )}
        </>
    );
};

export const OfferSalesOffices = (props: IProps) => {
    const {offer, property} = props;
    const {vendor} = props.offer;
    const isForSaleOverLimit = getOfferDisplayStatus(offer) === "limited";
    const hideForm = offer.sales_offices.length === 0;

    const firstSalesOffice = props.offer.sales_offices && props.offer.sales_offices[0];
    const gtmData = {
        clickText: InquiryButtonTypeName.ARRANGE_MEETING,
        source: GTMEventApplicationSourceSection.OFFER_WEBSITE,
        vendorName: offer.vendor.name ?? offer.vendor.id.toString(),
        offerName: offer.name ?? offer.id.toString()
    };

    return (
        <div className={offerSalesOfficesBase} data-testid={OFFER_TEST_IDS.OFFER_VIEW.OFFER_SALES_OFFICE_INFO_SECTION}>
            <div>
                {getThumbnailLink(offer.logo_url, 200, 200) ? (
                    <div className={css({display: "flex", justifyContent: "center", mb: 3})}>
                        <img
                            src={getThumbnailLink(offer.logo_url, 200, 200) ?? ""}
                            alt={`${offer.name} logo`}
                            width={160}
                            height={120}
                            loading="lazy"
                            data-testid={OFFER_TEST_IDS.OFFER_VIEW.VENDOR_LOGO}
                        />
                    </div>
                ) : (
                    <>
                        {getThumbnailLink(vendor.logo_url, 160, 120) && (
                            <img
                                src={getThumbnailLink(vendor.logo_url, 160, 120) ?? ""}
                                alt={`${vendor.name} logo`}
                                width={160}
                                height={120}
                                data-testid={OFFER_TEST_IDS.OFFER_VIEW.VENDOR_LOGO}
                            />
                        )}

                        <Text
                            align="center"
                            as="p"
                            variant="headline_5"
                            className={css({my: 3})}
                            dataTestId={OFFER_TEST_IDS.OFFER_VIEW.SALES_OFFICE_VENDOR_NAME}
                        >
                            {vendor.name}
                        </Text>
                    </>
                )}

                {offer.assistant ? (
                    <>
                        <Text variant="headline_6" as="p" className={css({my: 0.5})} align="center">
                            {offer.assistant.name}
                        </Text>
                        <Text variant="body_copy_2" as="p" className={css({mb: 2, mt: 0.5})} align="center">
                            OPIEKUN INWESTYCJI
                        </Text>
                    </>
                ) : null}

                {offer.website && (
                    <LinkHelper offer={offer} property={property} dataTestId={OFFER_TEST_IDS.OFFER_VIEW.OFFER_WEBSITE_LINK}>
                        <Highlight>
                            <Text variant="button_medium">Strona internetowa inwestycji</Text>
                        </Highlight>
                    </LinkHelper>
                )}

                {offer.website && (
                    <div className={css({mb: 2.5})}>
                        <LinkHelper offer={offer} property={property}>
                            <Text
                                className={offerLinkAsText}
                                variant="body_copy_2"
                                as="p"
                                align="center"
                                dataTestId={OFFER_TEST_IDS.OFFER_VIEW.OFFER_WEBSITE_URL}
                            >
                                {cleanUrl(offer.website)}
                            </Text>
                        </LinkHelper>
                    </div>
                )}

                {firstSalesOffice && renderSalesOffice(firstSalesOffice, isForSaleOverLimit, offer.is_phone_clip_enabled)}

                {!isForSaleOverLimit && offer.is_phone_clip_enabled && offer.configuration.phone_clip && renderPhoneButton(offer.configuration.phone_clip)}

                {!isForSaleOverLimit && !hideForm && (
                    <div className={css({display: "flex", justifyContent: "center"})}>
                        <ApplicationModalButton
                            source={property ? ApplicationSource.ApplicationPropertyScheduleMeeting : ApplicationSource.ApplicationOfferScheduleMeeting}
                            sourceSection={ApplicationSourceSection.BOTTOM}
                            vendorId={props.offer.vendor.id}
                            offerId={props.offer.id}
                            propertyId={property?.id ?? null}
                            forceClose={getOfferDisplayStatus(props.offer) === "limited"}
                            salesOfficeId={firstSalesOffice && firstSalesOffice.id}
                            disableUnlockChoice
                            render={({openModal}) => (
                                <Button
                                    variant="outlined_secondary"
                                    onClick={() => openModal(gtmData)}
                                    dataTestId={OFFER_TEST_IDS.OFFER_VIEW.MAKE_AN_APPOINTMENT_BUTTON}
                                >
                                    {InquiryButtonTypeName.ARRANGE_MEETING}
                                </Button>
                            )}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

const LinkHelper = ({
    offer,
    property,
    children,
    dataTestId
}: {
    offer: IProps["offer"];
    property: IProps["property"] | null;
    children: React.ReactNode;
    dataTestId?: string;
}) => {
    if (!offer.website) return null;

    const href = offer.configuration.include_utm
        ? appendQueryString(offer.website, {
              utm_campaign: "prezentacja",
              utm_medium: "referral",
              utm_source: "rynekpierwotny"
          })
        : offer.website;

    return (
        <Link
            className={css({mb: 1.5, display: "inline-block"})}
            href={href}
            target="_blank"
            rel="nofollow"
            data-gtm-click="Odwiedź stronę inwestycji"
            data-testid={dataTestId}
            onClick={() => {
                if (property) {
                    propertyViewExternalSiteClick(property, offer, offer.vendor);
                } else {
                    offerViewExternalSiteClick(offer, offer.vendor);
                }

                trackAmplitudeButtonClick({
                    buttonName: "view_developer_website",
                    value: "see_developer_website",
                    buttonLink: href,
                    buttonCategory: property ? "property_page" : "investment_page",
                    itemId: offer.id,
                    propertyId: property?.id
                });
            }}
        >
            {children}
        </Link>
    );
};

const offerSalesOfficesBase = css({
    p: 3,
    borderRadius: 2,
    backgroundColor: "var(--colors-white)",
    textAlign: "center"
});

const offerLinkAsText = css({
    display: "inline",
    background: "var(--colors-gray-100)",
    borderRadius: 1,
    padding: "0.4rem 0.8rem"
});

const salesOfficeInfo = css({
    mb: 3
});

const noteHolder = css({
    maxWidth: "50rem",
    margin: "0 auto 2.4rem auto"
});

const openHoursList = css({
    listStyle: "none",
    paddingLeft: 0,
    margin: "0 auto 2.4rem",
    "& > li": {
        padding: "0.2rem 0"
    }
});
